// ═══════════════════════════════════════════════════════════════════════
// COMPONENTES DA CASA · os que toda tela de funcionalidade usa
// ═══════════════════════════════════════════════════════════════════════
// Mudaram de casa em 07/10/2026, quando a Rotina Contábil passou a ser
// interface própria. São os componentes que o design system da família manda
// reusar (`~/.claude/rungoal-design-system.md`, seção 7): a marca d'água do
// título e os dois quadros de abertura de aba. Uma implementação só para as
// duas interfaces.

import React, { useState } from 'react';
import { G } from './design';


// ═══════════════════════════════════════════════════════════════════════
// ÍCONES E BOTÕES DA CASA
// ═══════════════════════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────────────────────
// <IconeInfo /> + <BotaoSaibaMais /> — padrão único de "Saiba mais" (04/07).
// NUNCA emoji · ícone temático "i" em círculo (SVG · traço da marca).
// Props: onClick · claro (texto/ícone brancos p/ fundo colorido) · contorno
// (pílula com borda) · tamanho (sm|md|lg) · seta · label.
// ─────────────────────────────────────────────────────────────────────────
const IconeInfo = ({ tamanho = 15, cor = 'currentColor' }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none"
    stroke={cor} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="11" x2="12" y2="16.5" />
    <circle cx="12" cy="7.7" r="0.7" fill={cor} stroke="none" />
  </svg>
);

const BotaoSaibaMais = ({ onClick, claro = false, contorno = false, tamanho = 'md', seta = true, label = 'Saiba mais', style: ov = {} }) => {
  const S = {
    sm: { f: 8.5, ic: 11, gap: 4, pad: '3px 8px' },
    md: { f: 11,  ic: 15, gap: 6, pad: '8px 14px' },
    lg: { f: 12,  ic: 16, gap: 7, pad: '9px 16px' },
  }[tamanho] || { f: 11, ic: 15, gap: 6, pad: '8px 14px' };
  const cor = claro ? '#FFF' : G.verde;
  const st = {
    display: 'inline-flex', alignItems: 'center', gap: S.gap, cursor: 'pointer',
    fontFamily: "'Outfit', sans-serif", fontSize: S.f, fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase', color: cor,
    background: 'transparent',
    border: contorno ? `1px solid ${claro ? 'rgba(255,255,255,0.55)' : G.verde}` : 'none',
    padding: contorno ? S.pad : 0,
    borderRadius: contorno ? 6 : 0,
    ...ov,
  };
  return (
    <button type="button" onClick={onClick} style={st}>
      <IconeInfo tamanho={S.ic} cor={cor} />
      {label}{seta ? ' →' : ''}
    </button>
  );
};

// Ícones temáticos inline (substituem emojis figurativos · regra sem-emojis).
// width/height em 1em → herdam o tamanho da fonte do elemento · currentColor
// herda a cor. verticalAlign alinha ao texto adjacente.
const _svgInline = { verticalAlign: '-0.12em', flexShrink: 0 };
const _svgProps = { width: '1em', height: '1em', viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round',
  style: _svgInline, 'aria-hidden': true };
const IconeLixeira = () => (
  <svg {..._svgProps}><path d="M4 7h16" /><path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
    <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
    <line x1="10" y1="11" x2="10" y2="18" /><line x1="14" y1="11" x2="14" y2="18" /></svg>
);
const IconeLapis = () => (
  <svg {..._svgProps}><path d="M4 20h4L18.5 9.5a2.12 2.12 0 0 0-3-3L5 17v3z" />
    <line x1="13.5" y1="6.5" x2="17" y2="10" /></svg>
);

// Botão de remoção padrão (30/07/2026). Antes, Formação de Preço e Planilha de
// Insumos usavam um "×" cinza sem contorno nem hover — invisível, e confundível
// com um "fechar". Aqui vira lixeira explícita que fica vermelha ao passar o
// mouse: quem remove entende que está removendo, e não descobre por acidente.
const BotaoRemover = ({ onClick, titulo = 'Remover' }) => {
  const [sobre, setSobre] = useState(false);
  return (
    <button onClick={onClick} title={titulo} aria-label={titulo}
      onMouseEnter={() => setSobre(true)} onMouseLeave={() => setSobre(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, padding: 0, cursor: 'pointer', borderRadius: 0,
        background: sobre ? '#FDE8E8' : 'transparent',
        border: `1px solid ${sobre ? '#C03030' : 'transparent'}`,
        color: sobre ? '#C03030' : G.txt2,
        transition: 'background .12s, border-color .12s, color .12s',
      }}>
      <IconeLixeira />
    </button>
  );
};
const IconeCadeado = () => (
  <svg {..._svgProps}><rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
);
const IconeAlerta = () => (
  <svg {..._svgProps}><path d="M12 3.5 21 19.5H3L12 3.5z" /><line x1="12" y1="9.5" x2="12" y2="14" />
    <circle cx="12" cy="16.8" r="0.6" fill="currentColor" stroke="none" /></svg>
);
// Fechar pop-up, janela ou menu (28/09/2026). Antes, nove pop-ups escreviam o
// caractere "×" dentro do botão: o design system pede ícone desenhado (7.13.7),
// e o caractere ainda se confundia com o "×" de remover, que já virou lixeira.
const IconeFechar = () => (
  <svg {..._svgProps}><line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" /></svg>
);
const IconeReverter = () => (
  <svg {..._svgProps}><path d="M4 4v5h5" /><path d="M4.5 9a8 8 0 1 1-1.5 6" /></svg>
);

// ═══════════════════════════════════════════════════════════════════════
// TOKENS DO BP60M · o design system da jornada Business Plan 60M
// ═══════════════════════════════════════════════════════════════════════
// Vêm para cá porque o rodapé de navegação das páginas de fonte usa os
// botões deles, e esse rodapé é das duas interfaces.
const BP60M_TOKENS = {
  // Cores semânticas (referenciam G para coerência com resto do app)
  cores: {
    feita:       G.verde,         // tarefa concluída
    atual:       G.laranja,       // tarefa em andamento
    bloqueada:   '#DCDCDC',       // tarefa futura
    bloqueadaBg: '#F0F0F0',
    erro:        G.vermelho,
    bg:          G.card,
    txt:         G.txt1,
    txtSub:      G.txt2,
    borda:       G.borda,
    bege:        '#F5F5F1',
  },
  // Tipografia
  tipo: {
    eyebrow: {
      fontSize: 10, fontWeight: 700, color: G.verde,
      letterSpacing: '0.14em', textTransform: 'uppercase',
    },
    tituloTarefa: {
      fontSize: 26, fontWeight: 800, color: G.txt1,
      letterSpacing: '-0.01em', lineHeight: 1, fontFamily: "'Outfit', sans-serif",
    },
    subtitulo: {
      fontSize: 14, color: G.txt2, lineHeight: 1.5, maxWidth: 760,
    },
  },
  // Layout
  layout: {
    paddingTela: '28px 32px 24px',
    radiusTela: 8,
    gapInterno: 18,
    minHeightConteudo: 280,
  },
  // Selos de status (4 variantes)
  selos: {
    'nao-iniciado': { bg: '#F0F0F0', cor: G.txt2,        border: `1px solid ${G.borda}`,  label: 'Não iniciado' },
    'em-edicao':    { bg: G.laranjaLight, cor: '#B45309', border: `1px solid ${G.laranja}`, label: 'Em edição' },
    'em-revisao':   { bg: G.laranja, cor: '#FFF',         border: 'none',                  label: 'Em revisão' },
    'aprovado':     { bg: G.verde, cor: '#FFF',           border: 'none',                  label: 'Aprovado' },
  },
  // Botões (6 variantes — todos mesma altura/forma)
  botoes: {
    base: {
      padding: '9px 14px', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      borderRadius: 0, cursor: 'pointer', transition: 'all 0.15s ease',
      fontFamily: "'Outfit', sans-serif",
      // Sem isto o rótulo quebra entre a seta e a palavra em janela estreita:
      // os botões são itens de uma fileira flexível e encolhem por padrão.
      whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 6,
    },
    primario:   { background: G.verde,    color: '#FFF', border: `2px solid ${G.verde}` },
    secundario: { background: '#FFF',     color: G.verde, border: `2px solid ${G.verde}` },
    revisar:    { background: G.laranja,  color: '#FFF', border: '2px solid #FFF',
                  boxShadow: `0 0 0 1px ${G.laranja}` },
    editar:     { background: '#FFF',     color: G.txt2, border: `2px solid ${G.borda}` },
    aprovar:    { background: G.verdeMid, color: '#FFF', border: '2px solid #FFF',
                  boxShadow: `0 0 0 1px ${G.verdeMid}`, fontWeight: 800 },
    rascunho:   { background: '#F0F0F0',  color: G.txt1, border: '2px solid transparent' },
  },
  // Barra de progresso (bolinhas)
  bolinha: {
    base: { width: 16, height: 16, borderRadius: '50%', border: '2px solid',
            transition: 'all 0.2s ease', cursor: 'pointer' },
    feita:      { background: G.verde,   borderColor: G.verde },
    atual:      { background: G.laranja, borderColor: G.laranja, transform: 'scale(1.20)',
                  boxShadow: `0 0 0 3px rgba(244,124,60,0.25)` },
    bloqueada:  { background: '#F0F0F0', borderColor: '#DCDCDC', opacity: 0.55, cursor: 'not-allowed' },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// TABELA DE PLANILHA · cabeçalho, célula e a largura que pode quebrar
// ═══════════════════════════════════════════════════════════════════════
const thPlan = {
  padding: '10px 14px', fontSize: 10, fontWeight: 700, color: G.txt2,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  borderBottom: `1px solid ${G.borda}`, whiteSpace: 'nowrap',
};
const tdPlan = {
  padding: '8px 14px', fontSize: 12, color: G.txt1, verticalAlign: 'top',
};
// Só a coluna LARGA quebra em duas linhas. As curtas — data, lote, código,
// valor — jamais: quebrar "02/01/2026" ao meio é o tipo de detalhe que
// desqualifica o documento inteiro aos olhos de um contador.
const COLUNA_LARGA = 40;   // caracteres

// Rodapé de navegação das páginas de fonte · idêntico ao das Tarefas do BP60M
// (← Anterior laranja sólido · ↩ Voltar outline · Avançar → verde). Fluxo normal.
const RodapeNavFonte = ({ onAnterior, onVoltar, onProximo, temAnterior, temProximo, rotuloVoltar = 'Dados de Entrada' }) => (
  <div style={{
    background: G.card, borderTop: `1px solid ${G.borda}`,
    padding: '10px 28px', flexShrink: 0, position: 'relative', zIndex: 10,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14,
  }}>
    <div style={{ display: 'flex', gap: 8 }}>
      <button onClick={onAnterior} disabled={!temAnterior}
        style={{
          ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.primario,
          background: G.laranja, color: '#FFF', border: `2px solid ${G.laranja}`,
          ...(!temAnterior ? { cursor: 'not-allowed', opacity: 0.45 } : {}),
        }}>
        ← Anterior
      </button>
      {/* Sem destino, o botão não aparece. A Rotina Contábil virou grupo de
          abas em 02/10 e não tem mais uma landing para onde voltar; botão que
          não leva a lugar nenhum é pior que botão ausente. */}
      {onVoltar && (
        <button onClick={onVoltar}
          style={{
            ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.editar,
            border: `2px solid ${G.laranja}`, color: G.laranja,
          }}>
          ↩ Voltar para {rotuloVoltar}
        </button>
      )}
    </div>
    <button onClick={onProximo} disabled={!temProximo}
      style={{
        ...BP60M_TOKENS.botoes.base, ...BP60M_TOKENS.botoes.primario,
        ...(!temProximo ? { cursor: 'not-allowed', opacity: 0.45 } : {}),
      }}>
      Avançar →
    </button>
  </div>
);

// Card de fonte de entrada (com hover)
const CardFonteEntrada = ({ fonte, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: '#FFF', border: `1px solid ${hover ? fonte.cor : G.borda}`,
        borderTop: `3px solid ${fonte.cor}`, borderRadius: 0, padding: '20px 22px',
        cursor: 'pointer', transition: 'all 0.15s ease',
        boxShadow: hover ? '0 6px 20px rgba(0,0,0,0.08)' : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: fonte.cor, letterSpacing: '0.10em',
          textTransform: 'uppercase' }}>{fonte.eyebrow}</div>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={fonte.cor}
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.85 }}>
          {fonte.icone}
        </svg>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: G.txt1, margin: '10px 0 6px' }}>
        {fonte.titulo}
        {fonte.selo && (
          <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 800, color: G.laranja,
            background: G.laranjaLight, border: `1px solid ${G.laranja}`, borderRadius: 0,
            padding: '2px 8px', letterSpacing: '0.06em', textTransform: 'uppercase',
            verticalAlign: 'middle' }}>{fonte.selo}</span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: G.txt2, lineHeight: 1.55 }}>{fonte.desc}</p>
      <div style={{ marginTop: 14, fontSize: 12, fontWeight: 800,
        color: fonte.cor, letterSpacing: '0.04em' }}>
        Abrir →
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// <MarcaDaguaTitulo /> — marca d'água do ícone do menu DEPOIS do título
// (era atrás · mudou em 07/06). Spec canônica: 100×100, 12px após a borda
// direita do título, alinhamento vertical bottom -50 (desceu 20px em 31/08,
// quando o nome da empresa saiu dos subtítulos), rotação 30°
// horário, verde brand opacity 0.13. Single source of truth — mudar aqui
// reflete em todas as telas que usam `<MarcaDaguaTitulo>{...paths}</...>`.
// Conteúdo SVG interno (paths/lines/rects) varia por tela.
// ═══════════════════════════════════════════════════════════════════════
const MarcaDaguaTitulo = ({ children, cor = G.verde, opacity = 0.13 }) => (
  <svg width="100" height="100" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{
      position: 'absolute',
      left: '100%',     // começa onde o título termina (direita do h2)
      marginLeft: 12,   // 12px de respiro entre fim do título e início do ícone
      bottom: -50,      // desceu 20px em 31/08 (subtítulos perderam o nome da empresa)
      color: cor,
      opacity,
      transform: 'rotate(30deg)',
      transformOrigin: 'center center',
      pointerEvents: 'none',
      zIndex: 0,
    }}>
    {children}
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════
// <QuadrosIntro/> — os 2 quadros de abertura de cada aba (30/07/2026)
// ═══════════════════════════════════════════════════════════════════════
// O padrão nasceu nas tarefas do BP60M, onde está copiado à mão umas 20 vezes.
// Aqui vira componente: verde = O QUE É (a definição) · laranja = O QUE VOCÊ
// FAZ AQUI (a ação do consultor). Mudar o desenho num lugar muda em todos.
//
// Não entra nas abas com landing page (Dados de Entrada · Custos Diretos),
// que já se apresentam pela própria capa.
const QuadrosIntro = ({ titulo, oQueE, oQueFaz }) => {
  const quadro = (cor, fundo, rotulo, corpo) => (
    <div style={{ padding: '12px 16px', background: fundo,
      borderLeft: `4px solid ${cor}`, borderRadius: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: cor,
        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {rotulo}
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.45, color: G.txt1 }}>{corpo}</div>
    </div>
  );
  return (
    // `flexShrink: 0` protege as telas scroll-zero (Dashboard · Valor), onde o
    // container é flex e comprimiria os quadros até ilegíveis.
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
      marginBottom: 18, flexShrink: 0 }}>
      {quadro(G.verde,   G.verdeLight,   `O que é ${titulo}`,     oQueE)}
      {quadro(G.laranja, G.laranjaLight, 'O que você faz aqui',   oQueFaz)}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// <SeloBreve /> — funcionalidade à vista que ainda não funciona
// ═══════════════════════════════════════════════════════════════════════
// O design system manda este selo, e proíbe "Em construção" (seção 7.15).
// O desenho é o do selo que a landing da Rotina Contábil já usava no
// `CardFonteEntrada`, com uma diferença: canto vivo, que é a forma da casa
// (seção 4.1). O arredondado de lá é tela antiga, e tela antiga só muda por
// tarefa própria.
const SeloBreve = ({ style = {} }) => (
  <span style={{
    display: 'inline-block', fontSize: 9, fontWeight: 800,
    color: G.laranja, background: G.laranjaLight,
    border: `1px solid ${G.laranja}`, borderRadius: 0,
    padding: '2px 8px', letterSpacing: '0.06em',
    textTransform: 'uppercase', verticalAlign: 'middle',
    whiteSpace: 'nowrap', ...style,
  }}>Breve</span>
);

export {
  IconeInfo, BotaoSaibaMais, IconeLixeira, IconeLapis, BotaoRemover,
  IconeCadeado, IconeAlerta, IconeFechar, IconeReverter,
  BP60M_TOKENS, thPlan, tdPlan, COLUNA_LARGA,
  RodapeNavFonte, CardFonteEntrada,
  MarcaDaguaTitulo, QuadrosIntro, SeloBreve,
};
